import React, { useEffect, useRef } from 'react';
import { useEnquiry } from '../context/EnquiryContext';

/**
 * A single catalogue photo — image unveils with a vertical wipe, caption sits
 * quietly beneath. Reports itself to the enquiry gate once actually seen,
 * which is what triggers the "more than 2 photos" prompt.
 */
export default function ProductCard({ product, contextLabel = '', onOpen, index = 0, priority = false }) {
  const { registerView } = useEnquiry();
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
          registerView(product.id, contextLabel, 'scroll');
          io.disconnect();
        }
      },
      { threshold: [0.45] }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [product.id, contextLabel, registerView]);

  const aspect = product.ratio === '9:16' ? 'aspect-[9/16]' : 'aspect-[4/5]';

  return (
    <article ref={ref} className="group">
      <button
        type="button"
        onClick={() => onOpen?.(index)}
        className="block w-full text-left"
        aria-label={`View ${product.name}`}
      >
        <div className={`relative overflow-hidden bg-beige/60 ${aspect}`}>
          <div className="wipe absolute inset-0">
            <img
              src={product.img}
              alt={product.name}
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.05]"
            />
          </div>

          {/* Quiet index mark */}
          <span className="pointer-events-none absolute left-4 top-4 text-[12px] tracking-wider2 text-cream/0 mix-blend-difference transition-colors duration-500 group-hover:text-cream/70">
            {String(index + 1).padStart(2, '0')}
          </span>

          {/* Hairline frame on hover */}
          <span className="pointer-events-none absolute inset-0 border border-royal/0 transition-colors duration-700 group-hover:border-royal/15" />
        </div>
      </button>

      {/* Stacked rather than a two-column row: a long metal string in a
          `justify-between` row cannot shrink and pushes the page sideways. */}
      <div className="border-t border-royal/10 pt-3.5">
        <h3 className="font-display text-[17px] leading-snug text-royal transition-colors duration-500 group-hover:text-gold-600">
          {product.name}
        </h3>
        {product.metal && (
          <span className="mt-1.5 block text-[11.5px] uppercase tracking-wider2 text-royal/75">
            {product.metal}
          </span>
        )}
      </div>
    </article>
  );
}
